<script setup lang="ts">
import { computed } from 'vue';
import {
  colorBelongsToPalette,
  normalizeBrandHex,
  type BrandColor,
  type ColorPickerMode,
} from '../brandPalette';

const props = withDefaults(defineProps<{
  modelValue: string;
  mode?: ColorPickerMode;
  colors?: BrandColor[];
  label?: string;
  manageUrl?: string;
  recentColors?: string[];
}>(), {
  mode: 'system',
  colors: () => [],
  label: 'Color',
  manageUrl: '',
  recentColors: () => [],
});

const emit = defineEmits<{ (event: 'update:modelValue', value: string): void }>();
const normalizedCurrent = computed(() => normalizeBrandHex(props.modelValue) ?? '#000000');
const libraryMode = computed(() => props.mode === 'library');
const currentInPalette = computed(() => colorBelongsToPalette(props.modelValue, props.colors));
const allowedRecent = computed(() => libraryMode.value
  ? props.recentColors.filter((color) => colorBelongsToPalette(color, props.colors))
  : props.recentColors);

function apply(value: string) {
  const normalized = normalizeBrandHex(value);
  if (!normalized) return;
  if (libraryMode.value && !colorBelongsToPalette(normalized, props.colors)) return;
  emit('update:modelValue', normalized);
}
</script>

<template>
  <div class="vptw:min-w-0" :data-color-picker-mode="mode">
    <label v-if="!libraryMode" class="vptw:flex vptw:items-center vptw:gap-2">
      <span
        class="vptw:relative vptw:size-9 vptw:shrink-0 vptw:overflow-hidden vptw:rounded-lg vptw:border vptw:border-(--vs-line)"
        :style="{ backgroundColor: normalizedCurrent }"
      >
        <input
          type="color"
          :aria-label="label"
          :value="normalizedCurrent"
          class="vptw:absolute vptw:inset-0 vptw:size-full vptw:cursor-pointer vptw:opacity-0"
          @input="apply(($event.target as HTMLInputElement).value)"
        >
      </span>
      <span class="vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ normalizedCurrent }}</span>
    </label>

    <template v-else-if="colors.length">
      <div class="vptw:flex vptw:flex-wrap vptw:gap-1.5" role="list" :aria-label="label">
        <button
          v-for="brand in colors"
          :key="brand.id"
          type="button"
          class="vptw:size-7! vptw:cursor-pointer vptw:rounded-lg! vptw:border! vptw:p-0!"
          :class="brand.hex === normalizedCurrent ? 'vptw:border-(--vs-accent)! vptw:ring-2 vptw:ring-(--vs-accent-soft)' : 'vptw:border-(--vs-line)!'"
          :style="{ backgroundColor: brand.hex }"
          :title="`${brand.title} · ${brand.hex}`"
          :aria-label="`${brand.title} ${brand.hex}`"
          @click="apply(brand.hex)"
        />
      </div>
      <p v-if="!currentInPalette" class="vptw:mt-1.5 vptw:mb-0 vptw:text-[10px] vptw:text-amber-700">
        Current {{ normalizedCurrent }} is outside Brand Colors. It stays unchanged until replacement.
      </p>
    </template>

    <p v-else class="vptw:m-0 vptw:rounded-lg vptw:bg-amber-50 vptw:p-2 vptw:text-xs vptw:text-amber-800">
      No published Brand Colors. Color changes disabled.
      <a v-if="manageUrl" :href="manageUrl" class="vptw:font-semibold vptw:text-amber-900">Manage Brand Colors</a>
    </p>

    <div v-if="allowedRecent.length && !libraryMode" class="vptw:mt-1.5 vptw:flex vptw:flex-wrap vptw:gap-1">
      <button
        v-for="color in allowedRecent"
        :key="color"
        type="button"
        class="vptw:size-5! vptw:cursor-pointer vptw:rounded-md! vptw:border! vptw:border-(--vs-line)! vptw:p-0!"
        :style="{ backgroundColor: color }"
        :title="`Recent · ${color}`"
        :aria-label="`Recent color ${color}`"
        @click="apply(color)"
      />
    </div>
  </div>
</template>
